import React, { useCallback, useState } from 'react';
import { Dropdown } from 'semantic-ui-react';
import styles from './styles/dashboardOverview.scss';
import { HelpTooltip } from 'components/tooltip';
import { FREQUENCY, FREQUENCY_OPTIONS } from 'constants/options';
import {
  prettifyNumber,
  addPremiums,
  calculateTotalInsured,
  getClosestRenewalPolicy,
  trackEvent,
  TRACKING_EVENTS,
} from 'helpers';
import { IPolicy, IUser } from 'types';
import { getRenewalOverview } from './utils';
import useFeatureExperiment from 'hooks/useFeatureExperiment';
import { Features } from 'constants/features';
import moment from 'moment';

interface IProps {
  adviser?: IUser;
  className?: string;
  policies: IPolicy[];
  collapse: boolean;
}

const overviewItems = [
  {
    id: 'DashboardOverview_TotalPolicies',
    label: 'TOTAL NUMBER OF POLICIES',
    getValue: (policies) => policies.length,
  },
  {
    id: 'DashboardOverview_NextPolicyRenewal',
    label: 'NEXT POLICY RENEWING IN',
    getValue: (policies) => {
      const policy = getClosestRenewalPolicy(policies);
      const value = getRenewalOverview(policies);
      if (policy) {
        return (
          <span>
            <b
              className={classnames(
                ['Overdue', 'Finish setup'].includes(value) && styles.red,
              )}
            >
              {value}
            </b>{' '}
            <span className={styles.subtext}>
              for {policy.policyType} insurance
            </span>
          </span>
        );
      }

      return value;
    },
  },
  {
    id: 'DashboardOverview_TotalPremiums',
    label: 'TOTAL PREMIUMS',
    getValue: (policies, frequency) =>
      `$${prettifyNumber(addPremiums(policies, frequency))}`,
    isFrequency: true,
  },
  {
    id: 'DashboardOverview_TotalSumInsured',
    label: 'TOTAL SUM INSURED',
    getValue: (policies) =>
      `$${prettifyNumber(
        R.prop('totalInsured', calculateTotalInsured(policies)),
      )}`,
    tooltip:
      'This captures the total value where there is a sum insured amount e.g. House, Vehicle, Contents, Life, etc.',
  },
];

const DashboardItem = ({
  label,
  id,
  isFrequency = false,
  tooltip = '',
  getClassName = R.F,
  getValue,
  policies,
  frequency,
  onFrequencyChange,
  onOpenHandler,
}) => {
  const value = getValue(policies, frequency);
  const valueClassName = getClassName(value);

  return (
    <div id={id} className={styles.item} key={label} onClick={onOpenHandler}>
      <div id={`${id}_Label`} className={styles.label}>
        {label}
        {tooltip && (
          <HelpTooltip
            className={styles.tooltip}
            toggleClassName={styles.toggle}
            content={tooltip}
            position="bottom center"
            onOpenHandler={onOpenHandler}
          />
        )}
      </div>
      <div
        id={`${id}_Value`}
        className={classnames(styles.value, valueClassName)}
      >
        {value}
        {isFrequency && (
          <Dropdown
            className={styles.dropdown}
            onChange={onFrequencyChange}
            options={FREQUENCY_OPTIONS}
            value={frequency}
            inline={true}
            icon="chevron down"
          />
        )}
      </div>
    </div>
  );
};

const DashboardOverview = ({
  className,
  adviser,
  policies,
  collapse,
}: IProps) => {
  const [frequency, setFrequency] = useState(FREQUENCY.YEARLY);
  const handleFrequencyChange = (_, { value }) => setFrequency(value);
  const hasAdviser = !!adviser;

  const totalPoints = policies.reduce((sum, item) => {
    const purchaseDate = item.purchaseDate || item.vehicle?.purchaseDate;
    if (
      purchaseDate &&
      moment(purchaseDate, 'YYYY-MM-DD').isAfter(
        moment('2025-07-31', 'YYYY-MM-DD'),
      )
    ) {
      sum += 100;
    }
    return sum;
  }, 0);

  return (
    <div
      className={classnames(
        styles.container,
        hasAdviser && styles.hasAdviser,
        collapse ? styles.collapse : styles.unCollapse,
        className,
      )}
    >
      <div className={styles.itemContainer}>
        {overviewItems.map((item) => (
          <DashboardItem
            key={item.label}
            {...item}
            policies={policies}
            frequency={frequency}
            onFrequencyChange={handleFrequencyChange}
          />
        ))}
      </div>
    </div>
  );
};

export default DashboardOverview;
